import { type RefObject, useLayoutEffect, useRef } from 'react';
import { _isNil, _isNotBlank } from '@/littledash';

const GLOBAL_TOOLTIP_ID = 'global-tooltip-id';

/**
 * Toggles the react-tooltip data attributes on a node based on whether its text overflows.
 * When truncated (and there is content) the shared global tooltip host reveals `content` on
 * hover; when it fits, the attributes are removed.
 */
const applyTruncationTooltip = (node: HTMLElement, content: string): void => {
  const truncated = node.scrollWidth > node.clientWidth;
  if (truncated && _isNotBlank(content)) {
    node.setAttribute('data-tooltip-id', GLOBAL_TOOLTIP_ID);
    node.setAttribute('data-tooltip-content', content);
  } else {
    node.removeAttribute('data-tooltip-id');
    node.removeAttribute('data-tooltip-content');
  }
};

/**
 * Attaches an overflow tooltip to an ellipsed element without triggering React re-renders.
 *
 * Returns a ref to place on the element carrying `text-overflow: ellipsis`. After every commit a
 * layout effect measures `scrollWidth > clientWidth` (before the browser paints) and imperatively
 * toggles the shared react-tooltip data attributes. Measuring on every commit covers both a text
 * change and a width change that leaves the text unchanged — e.g. a group header narrowing when
 * its parent column is collapsed, which re-renders the header. Driving the tooltip through DOM
 * attributes rather than React state avoids a re-render per column on every measurement.
 */
export function useTruncationTooltip<T extends HTMLElement>(content: string): RefObject<T | null> {
  const ref = useRef<T | null>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (_isNil(node)) {
      return;
    }
    applyTruncationTooltip(node, content);
  });

  return ref;
}
